import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  CreateUpdateHealthcareFacilityDto,
  GetHealthcareFacilityDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import {
  healthcareFacilityData,
  validate,
} from '../../../common/spdCore/contexts/HealthcareFacilityContext';

import { HealthcareFacilitySearch } from './HealthcareFacilitySearch';
import { HealthcareFacilityEditor } from './HealthcareFacilityEditor';

interface HealthcareFacilityListProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  isHistorical?: boolean;
}

export const HealthcareFacilityList: React.FC<HealthcareFacilityListProps> = ({
  getData,
  getCount,
  onRowClick,
  isHistorical = false,
}) => {
  const spd = useSpdCore();

  const [show, setShow] = useState(false);
  const [healthcareFacility, setHealthcareFacility] =
    useState<GetHealthcareFacilityDto>();
  const [healthcareFacilityDataId, setHealthcareFacilityDataId] = useState<
    string | undefined
  >();

  const [refresh, setRefresh] = useState<boolean>(false);

  const handleAdd = () => {
    setShow(true);
    setHealthcareFacility(undefined);
  };

  const addEditHF = async (
    hfCu: CreateUpdateHealthcareFacilityDto,
    id?: string,
  ) => {
    if (isHistorical && healthcareFacilityDataId) {
      hfCu.healthCareFacilityDataId = healthcareFacilityDataId;
    }

    const status = await validate(hfCu);
    if (status.valid) {
      try {
        if (id) {
          await spd.healthCareFacilities.update(id, hfCu);
        } else {
          await spd.healthCareFacilities.create(hfCu);
        }
        setShow(false);
        setHealthcareFacility(undefined);
        setHealthcareFacilityDataId(undefined);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleClose = () => {
    setShow(false);
    setHealthcareFacility(undefined);
    setHealthcareFacilityDataId(undefined);
    setRefresh(!refresh);
  };

  const handleClick = async (id: string) => {
    const [facilityId, dataId] = isHistorical ? id.split('|') : [id, null];

    if (!isHistorical) {
      const getHF = await spd.healthCareFacilities.get(facilityId);
      setHealthcareFacility(getHF);
    } else if (dataId !== null) {
      const getHF = await spd.healthCareFacilities.getSpecific(
        facilityId,
        dataId,
      );
      setHealthcareFacility(getHF);
      setHealthcareFacilityDataId(dataId);
    }

    setShow(true);
  };

  return (
    <>
      <HealthcareFacilitySearch
        getData={getData}
        getCount={getCount}
        onRowClick={onRowClick ? onRowClick : handleClick}
        refresh={refresh}
        isHistorical={isHistorical}
      />
      {!isHistorical && (
        <Button variant="outline-primary" onClick={handleAdd}>
          Nowa placówka
        </Button>
      )}
      <HealthcareFacilityEditor
        id={healthcareFacility?.id}
        healthcareFacility={healthcareFacilityData(healthcareFacility)}
        onCompleted={addEditHF}
        show={show}
        handleClose={handleClose}
        isHistorical={isHistorical}
      />
    </>
  );
};
